/**
 * PostHog Analytics — cookie-free, privacy-first tracking.
 *
 * Initialised only in production (or when VITE_POSTHOG_KEY is set).
 * Uses memory persistence (no cookies, no localStorage for tracking).
 * Never captures PII or financial values.
 */

import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://eu.i.posthog.com';

let initialised = false;

export function initPostHog(): void {
  if (initialised || !POSTHOG_KEY) return;

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      persistence: 'memory',              // No cookies, no localStorage
      autocapture: false,                  // We control what is captured
      capture_pageview: false,             // We fire page views manually via router
      capture_pageleave: true,             // Useful for session duration
      disable_session_recording: true,     // No session replay
      mask_all_text: true,                 // Belt-and-braces: mask DOM text in any capture
      mask_all_element_attributes: true,   // Mask attributes too
      sanitize_properties: (properties) => {
        if (!properties || typeof properties !== 'object') return properties;
        // Allowlist: only permit known-safe property keys through to PostHog.
        // This prevents accidental financial data leakage if a developer adds
        // new event properties without thinking about privacy.
        const allowed = new Set([
          // PostHog internals
          '$current_url', '$pathname', '$host', '$browser', '$device', '$os',
          '$screen_height', '$screen_width', '$viewport_height', '$viewport_width',
          '$referrer', '$referring_domain', '$session_id', '$window_id',
          'distinct_id', 'token', '$lib', '$lib_version', '$insert_id',
          '$time', '$set', '$set_once', 'event', 'timestamp',
          '$page_leave_properties', '$pageleave',
          // Our custom properties
          'step', 'planType', 'strategy', 'slug',
          'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
        ]);
        for (const key of Object.keys(properties)) {
          if (!key.startsWith('$') && !allowed.has(key)) {
            delete properties[key];
          }
        }
        return properties;
      },
    });
    initialised = true;
  } catch (error) {
    console.warn('[Analytics] PostHog init failed — analytics disabled for this session:', error);
  }
}

/**
 * Track a page view. Call on route changes.
 */
export function trackPageView(path: string): void {
  if (!initialised) return;
  try { posthog.capture('$pageview', { $current_url: path }); } catch { /* analytics non-critical */ }
}

/**
 * Track a custom event. Properties must never include financial values or PII.
 */
export function trackEvent(event: string, properties?: Record<string, string | number | boolean>): void {
  if (!initialised) return;
  try { posthog.capture(event, properties); } catch { /* analytics non-critical */ }
}

/**
 * Identify a registered user by opaque ID (NOT email).
 * Call after login/registration.
 */
export function identifyUser(userId: string): void {
  if (!initialised) return;
  try { posthog.identify(userId); } catch { /* analytics non-critical */ }
}

/**
 * Reset identity on logout.
 */
export function resetUser(): void {
  if (!initialised) return;
  try { posthog.reset(); } catch { /* analytics non-critical */ }
}

/**
 * Check if PostHog is active (for conditional UI, testing).
 */
export function isAnalyticsEnabled(): boolean {
  return initialised;
}
